import { useEffect, useState } from "react";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Bookmark, BookmarkPlus, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * SavedViews — frontend-only "saved filter" feature, persisted in localStorage.
 *
 * Props:
 *   storageKey: string — unique key per page (e.g. "kippu_views_tickets_v1")
 *   currentFilters: object — current filter state to be saved
 *   onApply: (filters) => void — invoked when user picks a saved view
 *   isEqual: (a, b) => bool (optional) — equality check; defaults to JSON.stringify
 */
export default function SavedViews({
  storageKey,
  currentFilters,
  onApply,
  isEqual,
}) {
  const [views, setViews] = useState([]);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) setViews(JSON.parse(raw));
    } catch (e) { console.warn("saved-views load failed:", e?.message); }
  }, [storageKey]);

  const persist = (next) => {
    setViews(next);
    try { localStorage.setItem(storageKey, JSON.stringify(next)); }
    catch (e) { console.warn("saved-views save failed:", e?.message); }
  };

  const eq = isEqual || ((a, b) => JSON.stringify(a) === JSON.stringify(b));
  const activeView = views.find((v) => eq(v.filters, currentFilters));

  const save = () => {
    const name = newName.trim();
    if (!name) return toast.error("Name required");
    if (views.some((v) => v.name.toLowerCase() === name.toLowerCase())) {
      return toast.error("A view with that name already exists");
    }
    persist([...views, { id: `v_${Date.now()}`, name, filters: currentFilters }]);
    setNewName("");
    setCreating(false);
    toast.success(`Saved view "${name}"`);
  };

  const remove = (id, e) => {
    e?.stopPropagation();
    const v = views.find((x) => x.id === id);
    persist(views.filter((x) => x.id !== id));
    toast.success(`Removed "${v?.name}"`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline" size="sm"
          className={`border-slate-200 ${activeView ? "text-indigo-700 border-indigo-200 bg-indigo-50/40" : "text-slate-700"}`}
          data-testid="saved-views-trigger"
        >
          <Bookmark className="w-3.5 h-3.5 mr-1.5" />
          {activeView ? activeView.name : "Saved views"}
          {views.length > 0 && (
            <Badge variant="outline" className="ml-1.5 text-[10px] bg-slate-50 text-slate-500">
              {views.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0" data-testid="saved-views-popover">
        <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Saved views</div>
          {!creating && (
            <Button
              size="sm" variant="ghost"
              className="h-7 text-xs text-indigo-600 hover:text-indigo-700"
              onClick={() => setCreating(true)}
              data-testid="saved-views-new-btn"
            >
              <BookmarkPlus className="w-3.5 h-3.5 mr-1" /> Save current
            </Button>
          )}
        </div>

        {creating && (
          <div className="px-3 py-3 border-b border-slate-100 bg-slate-50/40 space-y-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") { setCreating(false); setNewName(""); }
              }}
              placeholder="My open high-priority tickets"
              className="text-sm h-8"
              autoFocus
              data-testid="saved-views-name-input"
            />
            <div className="flex items-center justify-end gap-2">
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setCreating(false); setNewName(""); }}>
                Cancel
              </Button>
              <Button size="sm" className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700" onClick={save} data-testid="saved-views-save-btn">
                Save
              </Button>
            </div>
          </div>
        )}

        <div className="max-h-72 overflow-y-auto">
          {views.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-slate-400">
              No saved views yet. Tweak your filters then click <em>Save current</em>.
            </div>
          ) : (
            views.map((v) => {
              const isActive = activeView?.id === v.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => { onApply(v.filters); setOpen(false); }}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 group ${
                    isActive ? "bg-indigo-50/60 text-indigo-700" : "text-slate-700"
                  }`}
                  data-testid={`saved-view-${v.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {isActive ? <Check className="w-3.5 h-3.5 text-indigo-600 shrink-0" /> : <Bookmark className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                    <span className="truncate">{v.name}</span>
                  </div>
                  <Trash2
                    onClick={(e) => remove(v.id, e)}
                    className="w-3.5 h-3.5 text-slate-300 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    data-testid={`saved-view-delete-${v.id}`}
                  />
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
